import { ArrowDropDown as ArrowDropDownIcon, Cancel as CancelIcon, ClearOutlined } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { CHIP_COLOR_CLASS } from '@/components/fuel-autocomplete/FuelAutoComplete';
import { useVNFieldHelpContext, VNFieldHelpIconButton } from '@/components/VNFieldHelp';

import type { DisplaySlotProps, DropdownOption } from '../types';

type MultiChipRowProps = {
  selectedOptions: DropdownOption[];
  chipColorClass: string;
  onClearOption: (id: DropdownOption['id']) => void;
  chipsExpanded: boolean;
  onExpandChips: () => void;
};

/** First chip + "+N", or all chips when expanded (scroll inside the trigger border). */
export const VNDropdownMultiChipRow = ({
  selectedOptions,
  chipColorClass,
  onClearOption,
  chipsExpanded,
  onExpandChips,
}: MultiChipRowProps) => {
  const visibleOptions = chipsExpanded ? selectedOptions : selectedOptions.slice(0, 1);
  const overflow = chipsExpanded ? 0 : selectedOptions.length - 1;

  return (
    <div
      className={[
        'vn-dropdown-display__chips',
        chipsExpanded ? 'vn-dropdown-display__chips--expanded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {visibleOptions.map((option) => (
        <Chip
          key={option.id}
          label={option.label}
          title={option.label}
          size="small"
          className={chipColorClass}
          onDelete={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClearOption(option.id);
          }}
          deleteIcon={
            <CancelIcon sx={{ fontSize: 14 }} onMouseDown={(e: React.MouseEvent) => e.stopPropagation()} />
          }
        />
      ))}
      {overflow > 0 && (
        <span
          className="vn-dropdown-display__overflow-badge"
          role="button"
          tabIndex={-1}
          aria-label={`Show ${overflow} more selected`}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onExpandChips();
          }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

export function useVNDropdownChipsExpanded(isOpen: boolean, selectedOptions: DropdownOption[]) {
  const [userExpanded, setUserExpanded] = useState(false);
  const selectionKey = useMemo(() => selectedOptions.map((o) => o.id).join('\0'), [selectedOptions]);

  useEffect(() => {
    setUserExpanded(false);
  }, [selectionKey]);

  // Opening the list must not inherit a prior +N expand (keeps popper anchor stable for inset).
  useEffect(() => {
    if (isOpen) setUserExpanded(false);
  }, [isOpen]);

  return {
    /** Collapsed first chip + "+N" unless the user clicked +N while the trigger is closed. */
    chipsExpanded: userExpanded && !isOpen,
    /** Same +N expand inside the open autocomplete chip row (never auto-expand on multi-select). */
    chipsExpandedInOpenPanel: userExpanded,
    expandChips: () => setUserExpanded(true),
  };
};

/**
 * Default display slot for VNDropdown.
 *
 * Renders a styled button trigger with a floating label, selected value(s), and a caret icon.
 * Satisfies `DisplaySlotProps` so it can be replaced by a custom component.
 */
const VNDropdownDisplay = ({
  selectedOptions,
  isOpen,
  onToggle,
  onClearOption,
  label,
  placeholder = '',
  disabled = false,
  error,
  multiple = false,
  inputRef,
  triggerId,
  listboxId,
  focusedOptionId,
  onKeyDown,
  disableBorder = false,
  compact = false,
  allowClear = false,
  onClearAll,
  hideFloatingLabel = false,
  chipColor = 'default',
}: DisplaySlotProps) => {
  const { iconConfig: fieldHelpIconConfig } = useVNFieldHelpContext();
  const chipColorClass = CHIP_COLOR_CLASS[chipColor];
  const hasValue = selectedOptions.length > 0;
  const isLabelFloating = isOpen || hasValue || !!error;
  const errorId = `${triggerId}-error`;
  const showClearButton = allowClear && hasValue && !disabled;
  const { chipsExpanded, expandChips } = useVNDropdownChipsExpanded(isOpen, selectedOptions);

  const renderContent = () => {
    // Compact multi-select: show placeholder + count badge; no chips
    if (compact && multiple) {
      return (
        <div className="vn-dropdown-display__compact-multiselect">
          <span className="vn-dropdown-display__placeholder">{placeholder}</span>
          {selectedOptions.length > 0 && (
            <span className="vn-dropdown-display__count-badge" aria-label={`${selectedOptions.length} selected`}>
              {selectedOptions.length}
            </span>
          )}
        </div>
      );
    }

    if (!hasValue) {
      return (
        <span className="vn-dropdown-display__placeholder">
          {/* Only show placeholder text when the label is floating (or there is no label) */}
          {!label || isLabelFloating ? placeholder : ''}
        </span>
      );
    }

    if (multiple) {
      return (
        <VNDropdownMultiChipRow
          selectedOptions={selectedOptions}
          chipColorClass={chipColorClass}
          onClearOption={onClearOption}
          chipsExpanded={chipsExpanded}
          onExpandChips={expandChips}
        />
      );
    }

    return <span className="vn-dropdown-display__value">{selectedOptions[0].label}</span>;
  };

  return (
    <div
      className={[
        'vn-dropdown-trigger',
        isLabelFloating && label && !hideFloatingLabel ? 'vn-dropdown-trigger--label-floating' : '',
        error ? 'vn-dropdown-trigger--error' : '',
        disabled ? 'vn-dropdown-trigger--disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label && !hideFloatingLabel && (
        <label
          htmlFor={triggerId}
          className={['vn-dropdown-trigger__label', isLabelFloating ? 'vn-dropdown-trigger__label--floating' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {label}
        </label>
      )}

      <button
        id={triggerId}
        ref={inputRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={focusedOptionId}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? errorId : undefined}
        disabled={disabled}
        className={[
          'vn-dropdown-display',
          isOpen ? 'vn-dropdown-display--open' : '',
          chipsExpanded ? 'vn-dropdown-display--chips-expanded' : '',
          disabled ? 'vn-dropdown-display--disabled' : '',
          error ? 'vn-dropdown-display--error' : '',
          disableBorder || compact ? 'vn-dropdown-display--borderless' : '',
          showClearButton ? 'vn-dropdown-display--with-clear' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={onToggle}
        onKeyDown={onKeyDown}
      >
        <span className="vn-dropdown-display__content">{renderContent()}</span>
        <span className="vn-dropdown-display__trailing">
          <ArrowDropDownIcon
            className={['vn-dropdown-display__caret', isOpen ? 'vn-dropdown-display__caret--open' : ''].filter(Boolean).join(' ')}
            fontSize="small"
            aria-hidden="true"
          />
          {fieldHelpIconConfig && (
            <span
              className="vn-dropdown-trigger__field-help"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <VNFieldHelpIconButton {...fieldHelpIconConfig} />
            </span>
          )}
        </span>
      </button>

      {showClearButton && (
        <IconButton
          size="small"
          className="vn-dropdown-display__clear-btn"
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClearAll?.();
          }}
          aria-label="Clear selection"
          tabIndex={-1}
        >
          <ClearOutlined fontSize="small" />
        </IconButton>
      )}

      {error && (
        <p id={errorId} className="vn-dropdown-trigger__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default React.memo(VNDropdownDisplay);
